import { type JSONConfig } from '@/engine/app/interfaces.ts';
import * as defaultObjectModel from './_viewObjects.config.ts';
import * as defaultViewOptionsModel from './_viewOptions.config.ts';
import * as defaultViewOptionsGroups from './_viewOptionsGroup.config.ts';

import { viewPorts } from '@/surgeries/cardiac/TTVR/configs/views/_common.config.ts';
import { heartWorkflowIndications } from './_wfIndications.config.ts';
import { axial, coronal, sagittal } from '@/engine/common/imageSlices.config.ts';
import { SlicerMode, SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const config: JSONConfig.View = {
  descriptor: 'intra-op',
  label: 'Intra-op',
  sideLabel: 'Intra-op',
  imageStackDescriptor: 'Heart',
  imagesRoiConfig: {
    descriptor: 'device_roi',
    syncSliceDescriptor: SliceType.SAGITTAL,
    zoomFactor: 0.5
  },
  viewOrientationDescriptor: 'heart_orientation',
  fluoroCurvePlaneDescriptor: 'pl_annulus',
  viewOptions: [
    {
      ...defaultViewOptionsGroups.devices,
      children: [
        defaultViewOptionsModel.valve,
        defaultViewOptionsModel.catheter,
        defaultViewOptionsModel.additionalDevice,
        defaultViewOptionsModel.additionalStructure
      ]
    },
    {
      ...defaultViewOptionsGroups.rightHeart,
      children: [
        defaultViewOptionsModel.rightAtrium,
        defaultViewOptionsModel.rightVentricle,
        defaultViewOptionsModel.rightHeart,
        defaultViewOptionsModel.pulmonaryArtery,
        defaultViewOptionsModel.calcification
      ]
    },
    {
      ...defaultViewOptionsGroups.leftHeart,
      children: [
        defaultViewOptionsModel.leftAtrium,
        defaultViewOptionsModel.leftVentricle,
        defaultViewOptionsModel.aorta
      ]
    }
  ],
  objects: [
    defaultObjectModel.landingZoneSpline,
    defaultObjectModel.valve,
    defaultObjectModel.catheter,
    defaultObjectModel.leftAtrium,
    defaultObjectModel.leftVentricle,
    defaultObjectModel.rightAtrium,
    defaultObjectModel.rightVentricle,
    defaultObjectModel.rightHeart,
    { ...defaultObjectModel.aorta, isVisible: false },
    defaultObjectModel.pulmonaryArtery,
    defaultObjectModel.eustachianRidgeSpline,
    defaultObjectModel.additionalDevice,
    { ...defaultObjectModel.additionalStructure, isContourVisible: true },
    defaultObjectModel.veinsCalcification,
    defaultObjectModel.calcification
  ],
  measurementBoxes: [],
  objectBoxes: [],
  workflowIndications: [...heartWorkflowIndications],
  orientationPresets: [
    {
      descriptor: 'heart_orientation',
      label: 'Implant'
    },
    {
      descriptor: 'en_face_orientation',
      label: 'En Face'
    },
    {
      descriptor: 'bottom_up_orientation',
      label: 'Bottom Up'
    }
  ],
  measurements: [
    {
      descriptor: 'landing_zone_area',
      isPickable: false,
      isTunable: false,
      isVisible: true,
      isSelected: false
    },
    {
      descriptor: 'var_basal_rv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'tp.pl_svc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: false
    },
    {
      descriptor: 'tp.pl_ivc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: true
    },
    {
      descriptor: 'var_ra_height',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth_apex',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva_offset',
      isVisible: false,
      zoomFactor: 0.8
    }
  ],
  centerLines: [
    {
      descriptor: 'long_axis.centerline',
      isVisible: false
    }
  ],
  tuneControls: [],
  imageSlices: [axial, sagittal, { ...coronal, imageSlicerMode: SlicerMode.FLUORO }],
  viewPorts,
  visibilityDependencies: [],
  selectionDependencies: []
};
